import { ImagePlus, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { Button } from "../../../../components/ui/common/button";

interface SalesUnitImageUploadProps {
  imageUrl: string | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

/**
 * Compact per-sales-unit image control — thumbnail + upload/remove icon
 * buttons, sized for a table row rather than a form card (see
 * `ProductImageUpload` for the product-level equivalent). Controlled: the
 * caller owns `imageUrl` (backed by `salesUnitImageStorage` — the real API
 * has no per-unit image field, so this never touches the server).
 */
const SalesUnitImageUpload = ({ imageUrl, onUpload, onRemove }: SalesUnitImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded tw-border tw-border-dashed tw-border-border tw-bg-muted/40">
        {imageUrl ? (
          <img src={imageUrl} alt="Unit" className="tw-h-full tw-w-full tw-object-cover" />
        ) : (
          <ImagePlus className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="tw-h-8 tw-w-8"
        title="Upload unit image"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="tw-h-3.5 tw-w-3.5" />
      </Button>
      {imageUrl ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="tw-h-8 tw-w-8 tw-text-destructive hover:tw-text-destructive"
          title="Remove unit image"
          onClick={onRemove}
        >
          <Trash2 className="tw-h-3.5 tw-w-3.5" />
        </Button>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="tw-hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default SalesUnitImageUpload;
